import { reactive, ref, type Ref } from 'vue'
import { useEvent } from './useEvent'
import { computedWithControl } from '@vueuse/core'

export type KeyboardButton = {
  key: string
  down: boolean
}

type AllowedEvents = 'keypress' | 'keydown' | 'keyup' | 'key';

export type KeyboardListenerFun = (btn: KeyboardButton) => void | Promise<void>;

export type UseKeyboardState = {
  listeners: Record<string, Array<KeyboardListenerFun>>;
}

export type UseKeyboard = {
  buttonMap: Record<string, Ref<KeyboardButton>>
  buttons: Ref<Ref<KeyboardButton>[]>
  button: (key: string) => Ref<KeyboardButton>;
  down: (key: string | string[]) => boolean;
  addEventListener: (evType: AllowedEvents, fun: KeyboardListenerFun) => () => void;
}

const createButton = (key: string): Ref<KeyboardButton> => {
  return ref<KeyboardButton>({
    key,
    down: false
  })
}


export const useKeyboard = (): UseKeyboard => {
  const state = reactive<UseKeyboardState>({
    listeners: {}
  });

  const updateCount = ref<number>(0);

  const addEventListener = (evType: AllowedEvents, fun: KeyboardListenerFun) => {
    state.listeners[evType] = state.listeners[evType] || [];
    state.listeners[evType] = [...state.listeners[evType], fun];

    return () => {
      state.listeners[evType] = state.listeners[evType].filter(it => it !== fun);
    }
  }

  const emitEvent = async (evType: AllowedEvents, btn: KeyboardButton) => {
    state.listeners[evType] = state.listeners[evType] || [];
    return await Promise.all(state.listeners[evType].map(async (fun) => {
      return await fun(btn);
    }))
  }

  const buttonMap: Record<string, Ref<KeyboardButton>> = {}

  const buttons = computedWithControl(updateCount, (): Ref<KeyboardButton>[] => {
    return Object.values(buttonMap)
  })

  const getButton = (key: string): Ref<KeyboardButton> => {
    if (!buttonMap[key]) {
      buttonMap[key] = createButton(key)
    }
    return buttonMap[key]
  }

  const isDown = (key: string | string[]): boolean => {
    if (Array.isArray(key)) {
      const btns = key.map(k => getButton(k));
      return btns.filter(it => it.value.down === true).length >= key.length;
    }
    return getButton(key).value.down;
  }

  const setButton = (key: string, fun: (button: KeyboardButton) => KeyboardButton) => {
    const btn = getButton(key)
    btn.value = fun(btn.value);
    updateCount.value += 1;
  }

  useEvent('keydown', (ev) => {
    setButton(ev.key, (btn) => {
      btn.down = true
      emitEvent('keydown', btn);
      emitEvent('key', btn);
      return btn
    })
  })

  useEvent('keyup', (ev) => {
    setButton(ev.key, (btn) => {
      btn.down = false
      emitEvent('keyup', btn);
      return btn
    })
  });

  useEvent('keypress', (ev) => {
    const btn = getButton(ev.key);
    emitEvent('keypress', btn.value);
    emitEvent('key', btn.value);
  })

  return { buttonMap, buttons, button: getButton, down: isDown, addEventListener }
}
